import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, CreditCard, ShieldCheck, HandCoins } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function ConsultationModal({ open, onClose }) {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/payments/config").then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false }));
  }, [open]);

  useEffect(() => {
    // Load Razorpay checkout script
    if (!document.getElementById("razorpay-sdk")) {
      const s = document.createElement("script");
      s.id = "razorpay-sdk";
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.async = true;
      document.body.appendChild(s);
    }
  }, []);

  if (!open) return null;

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pay = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please provide your name and phone number.");
      return;
    }
    if (!cfg?.enabled) {
      // Fallback: submit as a free consultation request (lead)
      setSubmitting(true);
      try {
        await api.post("/leads", { ...form, service_type: "consultation_request", source_page: "consultation_modal" });
        toast.success("Consultation request received. Our team will reach out shortly.");
        onClose();
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Could not submit. Try again.");
      } finally {
        setSubmitting(false);
      }
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/payments/create-order", {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        notes: form.notes || null,
        service_type: "paid_consultation",
        amount: cfg.consultation_fee_paise,
      });

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: "Apurba Estate",
        description: "Property Consultation Booking",
        prefill: { name: form.name, contact: form.phone, email: form.email },
        theme: { color: "#183B66" },
        handler: async (res) => {
          try {
            await api.post("/payments/verify", {
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast.success("Payment successful. Our team will contact you shortly.");
            onClose();
          } catch (err) {
            toast.error("Payment verification failed. Please contact us.");
          }
        },
        modal: { ondismiss: () => setSubmitting(false) },
      };
      // eslint-disable-next-line no-undef
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not initiate payment.");
    } finally {
      setSubmitting(false);
    }
  };

  const amount = cfg?.consultation_fee_paise ? cfg.consultation_fee_paise / 100 : 499;

  return (
    <div className="fixed inset-0 z-[60] bg-[#101827]/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="consultation-modal">
      <div className="bg-white rounded-2xl w-full max-w-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#101827] text-white p-6 flex items-start justify-between">
          <div>
            <div className="ae-eyebrow mb-2">Priority Advisory</div>
            <h3 className="font-display text-2xl font-semibold">Book a Consultation</h3>
            <p className="text-white/70 text-sm mt-2">
              {cfg?.enabled
                ? <>Secure your 30-min advisory slot for <span className="text-[#C5A46D] font-semibold">₹{amount}</span>. Fee adjustable against future service.</>
                : <>Submit a consultation request. Our team will reach out within 1 business day.</>}
            </p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center" aria-label="Close" data-testid="consultation-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={pay} className="p-6 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <input className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3.5 py-2.5 text-sm" placeholder="Full Name *" value={form.name} onChange={update("name")} data-testid="consult-name" />
            <input className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3.5 py-2.5 text-sm" placeholder="Phone *" value={form.phone} onChange={update("phone")} data-testid="consult-phone" />
          </div>
          <input className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3.5 py-2.5 text-sm" placeholder="Email (optional)" value={form.email} onChange={update("email")} data-testid="consult-email" />
          <textarea className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3.5 py-2.5 text-sm min-h-[80px]" placeholder="What would you like to discuss?" value={form.notes} onChange={update("notes")} data-testid="consult-notes" />

          <div className="flex items-center gap-2 text-xs text-[#64748B] bg-[#F4F6F8] rounded-lg p-3">
            <ShieldCheck className="w-4 h-4 text-[#059669] shrink-0" />
            {cfg?.enabled ? "Payments are processed securely by Razorpay. We don't store card details." : "Payments are temporarily disabled — your request will be submitted as a free enquiry."}
          </div>

          <button type="submit" disabled={submitting} className="ae-btn-primary w-full justify-center" data-testid="consult-submit">
            {cfg?.enabled ? <><CreditCard className="w-4 h-4" /> Pay ₹{amount} & Book</> : <><HandCoins className="w-4 h-4" /> Request Consultation</>}
          </button>
        </form>
      </div>
    </div>
  );
}
